import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, Sparkles, Database, Cloud, Webhook, FileSpreadsheet, BarChart3, ShoppingCart, Truck, CreditCard, MessageSquare, Mail, Smartphone, Globe, Zap, CheckCircle2 } from 'lucide-react';
import { DemoModal } from '@/components/DemoModal';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

// Integration Category
const IntegrationCategory = ({ icon: Icon, color, title, description, integrations }) => (
  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center feature-icon ${color}`}>
        <Icon size={24} color="white" />
      </div>
      <div>
        <h3 className="text-gray-900 font-bold text-lg">{title}</h3>
        <p className="text-gray-500 text-sm">{description}</p>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {integrations.map((integration, i) => (
        <div key={i} className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
          <CheckCircle2 size={14} className="text-green-500" />
          <span>{integration}</span>
        </div>
      ))}
    </div>
  </div>
);

// Feature Card
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center flex-shrink-0">
      <Icon size={20} className="text-white" />
    </div>
    <div>
      <h4 className="text-gray-900 font-bold mb-1">{title}</h4>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  </div>
);

export default function IntegrationsPage() {
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  return (
    <div data-testid="integrations-page">
      <Navbar onDemoClick={() => setDemoModalOpen(true)} />
      <SEOHead title="Integrations - SAP, Oracle, Tally, WhatsApp, UPI & More" description="ChannelLoyalty integrates with SAP, Oracle, Tally ERP, WhatsApp Business, UPI payment rails, and custom DMS platforms. REST APIs for seamless channel loyalty data sync." keywords="loyalty platform ERP integration, SAP loyalty integration, WhatsApp loyalty program, UPI rewards payout, Tally integration" canonical="https://channelloyalty.ai/integrations" />
      
      <main>
        {/* Hero */}
        <section className="hero-full" style={{ paddingBottom: '60px' }}>
          <div className="container">
            <div className="text-center pt-8 max-w-4xl mx-auto">
              <div className="hero-badge mx-auto mb-6">
                <Sparkles size={16} />
                Enterprise Integrations
              </div>
              <h1 className="text-white mb-6">Connect Everything. Break Nothing.</h1>
              <p className="text-white/80 text-lg mb-8">
                ChannelLoyalty integrates with your <strong className="text-white">ERP, CRM, DMS, and payment systems</strong> — 
                plug into your existing stack without disruption.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <button onClick={() => setDemoModalOpen(true)} className="btn btn-primary">
                  View All Integrations <ArrowRight size={18} />
                </button>
                <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
                  <MessageCircle size={18} /> Talk to Expert
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Integration Categories */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <span className="section-badge">Integration Categories</span>
              <h2>Pre-Built Connectors for Every System</h2>
              <p>100+ integrations to connect your entire channel ecosystem.</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <IntegrationCategory
                icon={Database}
                color="#2C39B8"
                title="ERP Systems"
                description="Enterprise resource planning"
                integrations={["SAP", "Oracle", "Microsoft Dynamics", "Tally", "NetSuite", "Zoho"]}
              />
              <IntegrationCategory
                icon={ShoppingCart}
                color="pink"
                title="DMS & Order Systems"
                description="Distributor management"
                integrations={["Bizom", "RetailIO", "FieldAssist", "BeatRoute", "Salesforce", "Custom DMS"]}
              />
              <IntegrationCategory
                icon={CreditCard}
                color="cyan"
                title="Payment & Payout"
                description="Financial infrastructure"
                integrations={["UPI", "Razorpay", "PayU", "Paytm", "NEFT/RTGS", "Wallet APIs"]}
              />
              <IntegrationCategory
                icon={MessageSquare}
                color="orange"
                title="Communication"
                description="Partner engagement channels"
                integrations={["WhatsApp Business", "SMS Gateways", "Email (SendGrid)", "Push Notifications"]}
              />
              <IntegrationCategory
                icon={BarChart3}
                color="green"
                title="Analytics & BI"
                description="Business intelligence"
                integrations={["Power BI", "Tableau", "Looker", "Google Analytics", "Custom Dashboards"]}
              />
              <IntegrationCategory
                icon={Cloud}
                color="blue"
                title="Cloud & Infrastructure"
                description="Platform infrastructure"
                integrations={["AWS", "Azure", "Google Cloud", "Salesforce", "HubSpot", "Freshworks"]}
              />
            </div>
          </div>
        </section>

        {/* API First */}
        <section className="section-light section">
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="section-badge mb-4 inline-block">API-First Platform</span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Build Custom Integrations with Our APIs</h2>
                <p className="text-gray-600 mb-8">
                  Full REST API access with comprehensive documentation. Build custom integrations, 
                  automate workflows, and extend ChannelLoyalty to fit your exact needs.
                </p>
                
                <div className="space-y-6">
                  <FeatureCard
                    icon={Webhook}
                    title="Webhooks & Events"
                    description="Real-time event notifications for claims, payouts, and partner activities."
                  />
                  <FeatureCard
                    icon={FileSpreadsheet}
                    title="Bulk Data Import"
                    description="CSV, Excel, and API-based bulk uploads for sales data and partner onboarding."
                  />
                  <FeatureCard
                    icon={Zap}
                    title="Real-Time Sync"
                    description="Bi-directional sync with your systems — changes reflect instantly."
                  />
                </div>
              </div>
              
              {/* Code Block Mock */}
              <div className="bg-gray-900 rounded-2xl p-6 overflow-hidden">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-gray-500 text-sm ml-2">api_example.py</span>
                </div>
                <pre className="text-sm text-gray-300 overflow-x-auto">
{`import channelloyalty

# Initialize client
client = channelloyalty.Client(api_key="your_key")

# Create a reward claim
claim = client.claims.create(
    partner_id="P-12345",
    program_id="loyalty-q4",
    amount=5000,
    type="invoice_upload",
    metadata={
        "invoice_no": "INV-2025-001",
        "region": "North"
    }
)

# Process instant payout
payout = client.payouts.create(
    partner_id="P-12345",
    amount=5000,
    method="upi",
    upi_id="partner@upi"
)

print(f"Payout status: {payout.status}")
# Output: Payout status: completed`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Integration Process */}
        <section className="section-dark section">
          <div className="container">
            <div className="section-header">
              <span className="section-badge" style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}>Implementation</span>
              <h2 className="text-white">Go Live in Weeks, Not Months</h2>
              <p className="text-gray-400">Our integration team ensures smooth deployment with zero disruption.</p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6 mt-12">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center mx-auto mb-4 text-white text-xl font-bold">1</div>
                <h4 className="text-white font-bold mb-2">Discovery</h4>
                <p className="text-gray-400 text-sm">Map your systems and data flows</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center mx-auto mb-4 text-white text-xl font-bold">2</div>
                <h4 className="text-white font-bold mb-2">Configure</h4>
                <p className="text-gray-400 text-sm">Set up connectors and data mapping</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center mx-auto mb-4 text-white text-xl font-bold">3</div>
                <h4 className="text-white font-bold mb-2">Test</h4>
                <p className="text-gray-400 text-sm">Validate data sync and workflows</p>
              </div>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center mx-auto mb-4 text-white text-xl font-bold">4</div>
                <h4 className="text-white font-bold mb-2">Go Live</h4>
                <p className="text-gray-400 text-sm">Launch with ongoing support</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section">
          <div className="container relative z-10">
            <h2>Need a Custom Integration?</h2>
            <p>Our team will help you connect ChannelLoyalty to any system in your stack.</p>
            <div className="cta-buttons">
              <button onClick={() => setDemoModalOpen(true)} className="btn btn-primary">
                Talk to Integration Team <ArrowRight size={18} />
              </button>
              <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
                <MessageCircle size={18} /> WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
      <DemoModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </div>
  );
}
